import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { cloneDeep } from 'lodash'
import { Col, Row, Space } from 'antd'
import { PrimaryButton } from 'Components/button'
import { isFormRequired, isFormError, formValidation, validateEmail } from 'helpers/format'
import { selectCurrentUser } from 'Store/user/selector'
import { AppInput, MultiUploadForm } from 'Components/form'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/member-point/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import { getMemberDetailAPI, updateMemberAPI } from 'Store/member-point/api'
import { MemberDetailReq, UpdateMemberReq } from 'Store/member-point/types'
import { selectMemberDatail, selectUpdateActionEvent } from 'Store/member-point/selector'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
  disabled?: boolean
}

const SaleOrderCustomersDetail: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detailData,
    disabled = false,
  } = props
  const detailEvent = detailData?.event
  const detail = detailData?.data
  const isCreateMode = mode === 'create'
  const requireFeild = ['full_name', 'tel']
  const initForm = {
    address: '',
    documents: [],
    email: '',
    full_name: '',
    id: 0,
    remark: '',
    tel: '',
  } as UpdateMemberReq
  const [formData, setFormData] = useState(cloneDeep(initForm))
  const [formError, setFormError] = useState([] as string[])
  const dispatch = useDispatch()

  const validateForm = () => {
    const error = formValidation(requireFeild, formData)

    if (formData.email) {
      if (!validateEmail(formData.email)) {
        error.push('email_format')
      }
    }
    setFormError([...error])
    return error.length > 0 ? false : true
  }

  const onGetDetail = (): void => {
    const req: MemberDetailReq = {
      id,
    }
    dispatch(getMemberDetailAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  const onUpdate = (): void => {
    if (!validateForm()) return
    const req: UpdateMemberReq = {
      ...formData,
      id,
    }
    dispatch(updateMemberAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    // clear form data when user cloase drawer
    if (!visible) {
      setFormData(cloneDeep(initForm))
      setFormError([])
    }

    // get detail on "edit" mode
    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
    }
  }, [visible])

  useEffect(() => {
    if (mode === 'edit' && id > 0 && detail) {
      setFormData({
        documents: detail?.documents || [],
        address: detail?.address || '',
        email: detail?.email || '',
        full_name: detail?.full_name || '',
        id: detail?.id || 0,
        remark: detail?.remark || '',
        tel: detail?.tel || '',
      })
    }
  }, [detail])

  useEffect(() => {
    if (formError.length > 0) {
      validateForm()
    }
  }, [formData])

  const onUpdateData = (val: string, key: string) => {
    if (key === 'mobile' && val.length > 10) {
      return
    }

    if (key === 'tax_mobile' && val.length > 10) {
      return
    }

    if (key === 'tax_number' && val.length > 13) {
      return
    }

    setFormData({
      ...formData,
      [key]: val,
    })
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
  }

  const isRequired = (name: string) => {
    return isFormRequired(requireFeild, name)
  }

  const isError = (name: string) => {
    return isFormError(formError, name)
  }

  const title = isCreateMode ? 'เพิ่มข้อมูลสมาชิก' : 'แก้ไขข้อมูลสมาชิก'

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      <PrimaryButton text="ยืนยันการแก้ไขข้อมูล" onClick={onUpdate} />
    </Space>
  )

  return (
    <DetailDrawer
      title={title}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={disabled ? null : FooterContent}
      width={'50%'}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal event={[event]} enable={true} successCalback={onEventSuccess} errorCalback={clearEvent} />
      <Row justify="space-between" gutter={16}>
        <Col md={12} xs={24}>
          <AppInput
            name="full_name"
            label="ชื่อสมาชิก"
            placeHolder="ระบุชื่อ นามสกุล"
            isRequired={isRequired('full_name')}
            isError={isError('full_name')}
            value={formData.full_name}
            onChange={onUpdateData}
            resProps={{ disabled: !isCreateMode || disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="tel"
            label="เบอร์โทรศัพท์"
            isRequired={isRequired('tel')}
            isError={isError('tel')}
            value={formData.tel}
            onChange={onUpdateData}
            resProps={{ disabled: !isCreateMode || disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            name="email"
            label="อีเมล"
            errorMsg={isError('email_format') ? 'รูปแบบอีเมลให้ถูกต้อง' : 'อีเมล'}
            isRequired={isRequired('email')}
            isError={isError('email') || isError('email_format')}
            value={formData.email}
            onChange={onUpdateData}
            specialRequired={true}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            label="ที่อยู่"
            isRequired={isRequired('address')}
            isError={isError('address')}
            name="address"
            value={formData.address}
            onChange={onUpdateData}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={12} xs={24}>
          <AppInput
            label="หมายเหตุ"
            isRequired={isRequired('remark')}
            isError={isError('remark')}
            name="remark"
            value={formData.remark}
            onChange={onUpdateData}
            resProps={{ disabled: disabled }}
          />
        </Col>
        <Col md={24} xs={24}>
          <MultiUploadForm
            token={currentUser.accessToken}
            onUpdateData={onUpdateData}
            files={formData.documents}
            folder="member-documents"
            listType="text"
            isPrivate={true}
            name="documents"
            description="ขนาดไม่เกิน 5MB ต่อไฟล์ (.png .jpg .pdf)"
            isRequired={isRequired('documents')}
            isError={isError('documents')}
            label="เอกสาร (สามารถอัพโหลดได้หลายไฟล์)"
            readOnly={disabled}
          />
        </Col>
      </Row>
    </DetailDrawer>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectUpdateActionEvent(state),
    detailData: selectMemberDatail(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(SaleOrderCustomersDetail)
